import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { AgeingBandConfig } from '@/types/ageing-analysis';
import { useModeAwareQuery } from './useModeAwareQuery';

interface AgeingBandApiResponse {
  id: number;
  sequence: number;
  startDays: number;
  endDays: number | null;
  label: string;
  bandLabel: string;
  isActive: boolean;
  color: string;
}

const DEFAULT_AGEING_BANDS: AgeingBandConfig[] = [
  { id: 1, sequence: 1, startDays: 0, endDays: 30, label: 'Current', bandLabel: '0-30 Days', color: '#22c55e' },
  { id: 2, sequence: 2, startDays: 31, endDays: 60, label: '31-60 Days', bandLabel: '31-60 Days', color: '#eab308' },
  { id: 3, sequence: 3, startDays: 61, endDays: 90, label: '61-90 Days', bandLabel: '61-90 Days', color: '#f97316' },
  { id: 4, sequence: 4, startDays: 91, endDays: 120, label: '91-120 Days', bandLabel: '91-120 Days', color: '#ef4444' },
  {
    id: 5,
    sequence: 5,
    startDays: 121,
    endDays: null,
    label: 'Over 120 Days',
    bandLabel: '121+ Days',
    color: '#dc2626',
  },
];

export function useAgeingBands() {
  const apiConfig = useApiConfig();

  return useModeAwareQuery<AgeingBandConfig[], Error>(
    ['ageing-bands'],
    async ({ signal }) => {
      const url = `${apiConfig.apiBaseUrl}/api/v1/settings/aging-bands`;
      const headers = await apiConfig.getHeaders();

      const response = await fetch(url, { headers, signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch ageing bands: ${response.status}`);
      }

      const data: AgeingBandApiResponse[] = await response.json();

      return data
        .filter((x) => x.isActive)
        .sort((a, b) => a.sequence - b.sequence)
        .map((x) => ({
          id: x.id,
          sequence: x.sequence,
          startDays: x.startDays,
          endDays: x.endDays,
          label: x.label,
          bandLabel: x.bandLabel,
          color: x.color || '#6b7280',
        }));
    },
    DEFAULT_AGEING_BANDS,
    { staleTime: 5 * 60 * 1000 },
  );
}

export { DEFAULT_AGEING_BANDS };
